import { useRouter } from 'next/router'
import { Fragment, useEffect, useMemo } from 'react'

import { useFocusActionState } from 'src/providers/FocusActionProvider'
import { useUser } from 'src/providers/UserProvider'

import { ButtonLinkRebrand } from 'src/components/atoms/ButtonRebrand'
import { Card } from 'src/components/atoms/Card'
import { Seedling, Spade, Tick } from 'src/components/atoms/Icons'
import { IconProps } from 'src/components/atoms/Icons/types'
import {
  ButtonLink,
  ExternalLink,
  InternalLink
} from 'src/components/atoms/Link'
import {
  Heading2,
  Heading4,
  ParagraphNoSpace
} from 'src/components/atoms/Typography'

import { useGetApplicationQuery } from 'src/config/generated/graphql'
import {
  createApplicationPath,
  createProjectPath,
  dashboardPath,
  selectProjectPath,
  viewApplicationPath,
  viewProjectDetailPath
} from 'src/config/paths'

type PlanterStepType = 'registration' | 'project' | 'request'

type PlanterStepBase = {
  type: PlanterStepType
}

type PlanterProjectStep = PlanterStepBase & {
  type: 'request'
  nextText?: never
  cta?: never
  title?: never
}

type PlanterOtherStep = PlanterStepBase & {
  type: 'registration' | 'project'
  title: string
  intro: string
  nextText: string
  cta: { text: string; href: string }
  cancelText?: string
}

type PlanterStep = PlanterProjectStep | PlanterOtherStep

type PlanterStepState = 'complete' | 'default' | 'disabled'

type PlanterStepSuccessProps = {
  step: PlanterStepType
}

type PlanterStepIndicatorProps = {
  icon: React.ComponentType<IconProps>
  text: string
  state: PlanterStepState
}

const PlanterStepIndicator = ({
  icon,
  text,
  state
}: PlanterStepIndicatorProps) => {
  const Icon = state === 'complete' ? Tick : icon
  return (
    <div
      className={`flow-stepper-step bg-transparent! p-0! border-0! ${state} flex-1 flex flex-col gap-[5px] items-center self-start`}
    >
      <div className='flow-stepper-icon w-9 h-9 border-[3px] rounded-full flex flex-row justify-center items-center'>
        <Icon className='w-5 h-5' />
      </div>
      <span
        className={`w-full text-center text-[14px] md:text-[16px] leading-[20px] md:leading-[24px] font-semibold ${
          state === 'disabled' ? 'text-oliveAsh' : 'text-mostlyGreen'
        }`}
      >
        {text}
      </span>
    </div>
  )
}

const PlanterStepSuccess = ({ step }: PlanterStepSuccessProps) => {
  const router = useRouter()
  const { user } = useUser()
  const { setFocussedHeader } = useFocusActionState()

  useEffect(() => {
    setFocussedHeader(() => {
      router.push(
        dashboardPath({
          queryParams: {
            selectedTab: 'planter'
          }
        })
      )
    })
  }, [router, setFocussedHeader])

  const { projectId, applicationId } = router.query as {
    projectId: string
    applicationId: string
  }
  const { data: applicationData } = useGetApplicationQuery({
    variables: {
      id: applicationId,
      planterId: user?.Account?.PlanterId
    },
    skip: !projectId || !applicationId || !user?.Account?.PlanterId
  })

  const projectCount = useMemo(
    () => user?.Account?.Planter?.ProjectsCount,
    [user]
  )

  const showStepIndicator = useMemo(() => projectCount === 1, [projectCount])

  const successSteps = useMemo<Array<PlanterStepIndicatorProps>>(
    () => [
      {
        icon: Seedling,
        text: '1. Planting experience',
        state: 'complete'
      },
      {
        icon: Spade,
        text: '2. Planting project',
        state: step === 'registration' ? 'default' : 'complete'
      },
      {
        icon: Spade,
        text: '3. Request trees',
        state: step === 'project' ? 'default' : 'disabled'
      }
    ],
    [step]
  )

  const planterStepParams = useMemo<Array<PlanterStep>>(
    () => [
      {
        type: 'registration',
        title: 'Thank you',
        intro: 'Your planting experience was successfully added.',
        nextText: 'You’re just one step away from requesting trees!',
        cta: {
          text: 'Add a planting project',
          href: createProjectPath()
        }
      },
      {
        type: 'project',
        title: 'Thank you',
        intro:
          projectCount === 1
            ? 'Your planting project was successfully added.'
            : 'Your new planting project has been successfully created.',
        nextText:
          projectCount === 1
            ? 'You can now add a tree request for your first project.'
            : 'To bring it to life, you can now request native trees for this project. ',
        cta: {
          text: projectCount === 1 ? 'Request trees' : 'Request trees now',
          href: projectId
            ? createApplicationPath({
                routeParams: {
                  projectId
                }
              })
            : selectProjectPath
        },
        cancelText: "I'll request trees later"
      },
      {
        type: 'request'
      }
    ],
    [projectId, projectCount]
  )

  const stepParams = planterStepParams.find(s => s.type === step)!

  if (stepParams.type === 'registration' || stepParams.type === 'project') {
    return (
      <div className='flex flex-col gap-5 min-h-dvh'>
        <div className='flex flex-col gap-[10px] items-center'>
          <Heading2 className='m-0! text-[26px]! leading-[34px]! md:text-[40px]! md:leading-[50px]! font-bold!'>
            {stepParams.title}
          </Heading2>
          <ParagraphNoSpace className='font-medium text-[16px]! md:text-[20px]! leading-[24px]! md:leading-[30px]! text-center'>
            {stepParams.intro}
          </ParagraphNoSpace>
        </div>
        <Card className='flex flex-col items-center'>
          {showStepIndicator ? (
            <div className='w-full mb-5 md:mb-10 flex flex-row items-center'>
              {successSteps.map((s, idx, arr) => (
                <Fragment key={idx}>
                  <PlanterStepIndicator
                    icon={s.icon}
                    state={s.state}
                    text={s.text}
                    key={s.state}
                  />
                  {idx < arr.length - 1 ? (
                    <div className='flex flex-col justify-center'>
                      <hr
                        className={`block border ${
                          arr[idx + 1]?.state === 'disabled'
                            ? 'border-oliveAsh'
                            : 'border-mostlyGreen'
                        } h-px -mx-4 sm:-mx-8 md:-mx-16 lg:-mx-20 -translate-y-6 sm:-translate-y-4`}
                      />
                    </div>
                  ) : null}
                </Fragment>
              ))}
            </div>
          ) : null}
          {stepParams.nextText ? (
            <ParagraphNoSpace className='mb-5 text-[14px]! leading-[20px]! md:text-[16px]! md:leading-[24px]! font-medium'>
              {stepParams.nextText}
            </ParagraphNoSpace>
          ) : null}
          {stepParams.cta ? (
            <div className='flex flex-col gap-5 items-center'>
              <ButtonLinkRebrand href={stepParams.cta.href}>
                {stepParams.cta.text}
              </ButtonLinkRebrand>
              <InternalLink
                className='font-semibold!'
                href={dashboardPath({
                  queryParams: {
                    selectedTab: 'planter'
                  }
                })}
              >
                {stepParams.cancelText || "I'll do it later"}
              </InternalLink>
            </div>
          ) : null}
        </Card>
        {stepParams.type === 'project' ? (
          <div className='text-center font-medium text-[16px] leading-[24px]'>
            Need help?{' '}
            <ExternalLink
              className='underline'
              href='https://help.treesthatcount.co.nz/en/articles/8514952-requesting-native-trees-for-my-planting-project'
            >
              Learn more about requesting trees
            </ExternalLink>
          </div>
        ) : null}
      </div>
    )
  }
  return (
    <div className='flex flex-col gap-5 min-h-dvh'>
      <div className='flex flex-col gap-[10px] items-center'>
        <Heading2 className='m-0! text-[26px]! leading-[34px]! md:text-[40px]! md:leading-[50px]! font-bold!'>
          Thank you
        </Heading2>
        <ParagraphNoSpace className='font-medium text-[16px]! md:text-[20px]! leading-[24px]! md:leading-[30px]! text-center'>
          Your tree request for your{' '}
          {applicationData?.Application[0]?.Project?.Name} project was
          successfully submitted.
        </ParagraphNoSpace>
      </div>
      <Card className='flex flex-col gap-5 md:gap-10 w-full'>
        <div className='flex flex-col gap-5'>
          <Heading4>Tree request summary</Heading4>
          <div className='flex flex-col p-5 bg-linen rounded-[5px]'>
            <dl className='font-medium'>
              <dt className='inline mr-2'>Project:</dt>
              <dd className='inline after:block after:mb-[10px]'>
                {applicationData?.Application[0]?.Project?.Name}
              </dd>

              <dt className='inline mr-2'>Planting season:</dt>
              <dd className='inline after:block after:mb-[10px]'>
                {applicationData?.Application[0]?.Season?.Name}
              </dd>

              <dt className='inline mr-2'>Trees requested:</dt>
              <dd className='inline after:block after:mb-[10px]'>
                {applicationData?.Application[0]?.Required}
              </dd>

              <dt className='inline mr-2'>Request reference:</dt>
              <dd className='inline after:block'>
                {applicationData?.Application[0]?.Reference}
              </dd>
            </dl>
          </div>
        </div>
        <div>
          <Heading4>You can view your request anytime</Heading4>
          <ul>
            <li>
              <ButtonLink
                className='no-underline hover:underline text-[14px] md:text-[16px]'
                variant='text'
                href={viewProjectDetailPath({
                  routeParams: {
                    projectId
                  }
                })}
              >
                Planting project details
              </ButtonLink>
            </li>
            <li>
              <ButtonLink
                className='no-underline hover:underline text-[14px] md:text-[16px]'
                variant='text'
                href={viewApplicationPath({
                  routeParams: {
                    applicationId,
                    projectId
                  }
                })}
              >
                Tree request (ref: {applicationData?.Application[0]?.Reference})
              </ButtonLink>
            </li>
          </ul>
        </div>
        <div className='self-center'>
          <ButtonLinkRebrand
            variant='outline'
            href={dashboardPath({
              queryParams: {
                selectedTab: 'planter'
              }
            })}
          >
            Close
          </ButtonLinkRebrand>
        </div>
      </Card>
    </div>
  )
}

export default PlanterStepSuccess
